import React, {
    forwardRef,
    ReactElement,
    useCallback,
    useEffect,
    useImperativeHandle,
    useState
} from "react";
import * as Yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { classNames } from "primereact/utils";
import { yupResolver } from "@hookform/resolvers/yup";
import { InputText } from "primereact/inputtext";
import { RadioButton } from "primereact/radiobutton";
import { Dropdown } from "primereact/dropdown";

import { RequestFormRef, RequestFormType } from "../types";
import { HttpService } from "../../../../services/HttpService";
import { InputMask } from "primereact/inputmask";

import useFleg from "../../../../assets/images/usa-flag.png"
import ReactGoogleAutocomplete from "react-google-autocomplete";
type PropTypes = {
    formData:RequestFormType,
    onSubmit: (formData:any) => void
};


const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

const RequestForm = forwardRef((props: PropTypes, ref: React.Ref<RequestFormRef>): ReactElement => {

    const {
        onSubmit,
        formData
    } = props;

    const [states, setStates] = useState<Record<string, string>[]>([]);
    const [countries, setCountries] = useState<Record<string, string>[]>([]);
    const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
    
    const {
        control,
        handleSubmit,
        formState: { errors },
    } = useForm<RequestFormType>({
        mode: 'onChange',
        resolver: yupResolver(
            Yup.object().shape({
                fullName: Yup.string().required('This field is required'),
                deliveryAddress: Yup.object({
                    city: Yup.string().required('This field is required'),
                    state: Yup.string().required('This field is required'),
                    countryCode: Yup.string().required('This field is required'),
                    zipCode: Yup.string().matches(
                        /^(\d{5}|\d{5}-\d{4})$/,
                        'Zip Code format must be 12345 or 12345-1234'
                    ).required('This field is required'),
                    addressLine1: Yup.string().required('This field is required'),
                    addressLine2: Yup.string(), // optional
                    phoneNumber: Yup.string().required('This field is required'),
                    addressType: Yup.string().oneOf(['RESIDENTIAL', 'BUSINESS']).required('This field is required'),
                }),
            })),
        defaultValues: {
            ...formData
        },
    });


useImperativeHandle(ref, () => {
    return {
        submit: handleSubmit(onSubmit)
    };
}, [handleSubmit, onSubmit]);

    const getCountriesANdStates = useCallback(async () => {
        const states = await HttpService.get('/api/public/ref/state').then(res => res.data);
        const countries = await HttpService.get('/api/public/ref/country').then(res => res.data);
        return {countries, states};
    },[]);

    useEffect(() => {
        getCountriesANdStates().then(data => {
            setStates(data.states);
            setCountries(data.countries);
            setIsDataLoaded(true);
        });
    },[]);

    return (
        <form  className="flex flex-column gap-3">
            <div className="w-full flex flex-column gap-2">
                <label htmlFor="fullName" className={classNames("font-medium", { "text-red-500": errors.fullName })}>
                    Full Name
                    <span className="ml-1 text-red-300">*</span>
                </label>
                <Controller
                    name="fullName"
                    control={control}
                    render={({ field, fieldState }) => {
                        return (
                            <>
                                <InputText
                                    {...field}
                                    id="fullName"
                                    className={classNames({
                                        'p-invalid': fieldState.invalid,
                                        'text-red-500': fieldState.invalid,
                                    })} />
                                {
                                    fieldState.invalid ? (
                                        <small className="text-red-500">
                                            {fieldState.error?.message}
                                        </small>
                                    ) : null
                                }
                            </>
                        )
                    }}
                />
            </div>

            <div className="w-full flex flex-column gap-2">
                <label htmlFor="addressLine1" className={classNames("font-medium", { "text-red-500": errors.deliveryAddress?.addressLine1 })}>
                    Address Line 1
                    <span className="ml-1 text-red-300">*</span>
                </label>
                <Controller
                    name="deliveryAddress.addressLine1"
                    control={control}
                    render={({ field, fieldState }) => {
                        return (
                            <>
                                {/* <ReactGoogleAutocomplete
                                    className="p-inputtext p-component"
                                    apiKey={googleMapsApiKey}
                                    placeholder="Enter your address"
                                    defaultValue={field.value}
                                    onPlaceSelected={(place) => {
                                        field.onChange(place.formatted_address);
                                    }}
                                    options={{
                                        types: ['address'],
                                        componentRestrictions: { country: 'us' },
                                    }}
                                    onChange={(e) => field.onChange((e.target as HTMLInputElement).value)}
                                /> */}

                                <InputText
                                    {...field}
                                    id="addressLine1"
                                    className={classNames({
                                        'p-invalid': fieldState.invalid,
                                        'text-red-500': fieldState.invalid,
                                    })} />
                                {
                                    fieldState.invalid ? (
                                        <small className="text-red-500">
                                            {fieldState.error?.message}
                                        </small>
                                    ) : null
                                }
                            </>
                        )
                    }}
                />
            </div>

            <div className="w-full flex flex-column gap-2">
                <label htmlFor="addressLine2" className={classNames("font-medium", { "text-red-500": errors.deliveryAddress?.addressLine2 })}>
                    Address Line 2 (Optional)
                </label>
                <Controller
                    name="deliveryAddress.addressLine2"
                    control={control}
                    render={({ field, fieldState }) => {
                        return (
                            <>
                                <InputText
                                    {...field}
                                    id="addressLine2"
                                    className={classNames({
                                        'p-invalid': fieldState.invalid,
                                        'text-red-500': fieldState.invalid,
                                    })} />
                                {
                                    fieldState.invalid ? (
                                        <small className="text-red-500">
                                            {fieldState.error?.message}
                                        </small>
                                    ) : null
                                }
                            </>
                        )
                    }}
                />
            </div>
            <div className="w-full flex gap-3 pt-1">
                <div className="w-4 p-1 -my-2 flex flex-column gap-2">
                    <label htmlFor="city" className={classNames("font-medium", { "text-red-500": errors.deliveryAddress?.city })}>
                        City
                        <span className="ml-1 text-red-300">*</span>
                    </label>
                    <Controller
                        name="deliveryAddress.city"
                        control={control}
                        render={({ field, fieldState }) => {
                            return (
                                <>
                                    <InputText
                                        {...field}
                                        id="city"
                                        className={classNames({
                                            'p-invalid': fieldState.invalid,
                                            'text-red-500': fieldState.invalid,
                                        })} />
                                    {
                                        fieldState.invalid ? (
                                            <small className="text-red-500">
                                                {fieldState.error?.message}
                                            </small>
                                        ) : null
                                    }
                                </>
                            )
                        }}
                    />
                </div>
                <div className="w-4 p-1 -my-2 flex flex-column gap-2">
                    <label htmlFor="state" className={classNames("font-medium", { "text-red-500": errors.deliveryAddress?.state })}>
                        State
                        <span className="ml-1 text-red-300">*</span>
                    </label>
                    <Controller
                        name="deliveryAddress.state"
                        control={control}
                        render={({ field, fieldState }) => {
                            return (
                                <>
                                    <Dropdown
                                        {...field}
                                        filter
                                        showClear
                                        disabled={!isDataLoaded}
                                        style={{ width: '100%' }}
                                        options={states}
                                        id="state"
                                        optionLabel="label"
                                        optionValue="label"
                                        placeholder="Select a State"
                                        className={classNames({
                                            'p-invalid': fieldState.invalid,
                                            'text-red-500': fieldState.invalid,
                                        })}
                                    />
                                    {
                                        fieldState.invalid ? (
                                            <small className="text-red-500">
                                                {fieldState.error?.message}
                                            </small>
                                        ) : null
                                    }
                                </>
                            )
                        }}
                    />
                </div>
                <div className="w-4 p-1 -my-2 flex flex-column gap-2">
                    <label htmlFor="countryCode" className={classNames("font-medium", { "text-red-500": errors.deliveryAddress?.countryCode })}>
                        Country
                        <span className="ml-1 text-red-300">*</span>
                    </label>
                    <Controller
                        name="deliveryAddress.countryCode"
                        control={control}
                        render={({ field, fieldState }) => {
                            return (
                                <>
                                    <Dropdown
                                        {...field}
                                        filter
                                        disabled
                                        showClear
                                        style={{ width: '100%' }}
                                        options={countries}
                                        id="countryCode"
                                        optionLabel="label"
                                        optionValue="code"
                                        placeholder="Select a Country"
                                        className={classNames({
                                            'p-invalid': fieldState.invalid,
                                            'text-red-500': fieldState.invalid,
                                        })}
                                    />
                                    {
                                        fieldState.invalid ? (
                                            <small className="text-red-500">
                                                {fieldState.error?.message}
                                            </small>
                                        ) : null
                                    }
                                </>
                            )
                        }}
                    />
                </div>                
            </div>

            <div className="w-full flex gap-3 pt-1">
                <div className="w-6 p-1 -my-2 flex flex-column gap-2">
                    <label htmlFor="zipCode" className={classNames("font-medium", { "text-red-500": errors.deliveryAddress?.zipCode })}>
                        Zip Code
                        <span className="ml-1 text-red-300">*</span>
                    </label>
                    <Controller
                        name="deliveryAddress.zipCode"
                        control={control}
                        render={({ field, fieldState }) => {
                            return (
                                <>
                                    <InputText
                                        {...field}
                                        id="zipCode"
                                        keyfilter="num"
                                        className={classNames({
                                            'p-invalid': fieldState.invalid,
                                            'text-red-500': fieldState.invalid,
                                        })} />
                                    {
                                        fieldState.invalid ? (
                                            <small className="text-red-500">
                                                {fieldState.error?.message}
                                            </small>
                                        ) : null
                                    }
                                </>
                            )
                        }}
                    />
                </div>
                <div className="w-6 p-1 -my-2 flex flex-column gap-2">
                    <label htmlFor="phoneNumber" className={classNames("font-medium", { "text-red-500": errors.deliveryAddress?.phoneNumber })}>
                        Phone Number
                        <span className="ml-1 text-red-300">*</span>
                    </label>
                    <Controller
                        name="deliveryAddress.phoneNumber"
                        control={control}
                        render={({ field, fieldState }) => {
                            return (
                                <>
                                        <div className="p-inputgroup">
                                            <span className="p-inputgroup-addon bg-white">
                                                        <img src={useFleg}  width={24} height={16}/>
                                            </span>
                              
                                        <InputMask
                                            {...field}
                                            id="phoneNumber"
                                            mask="(999) 999-9999"
                                            placeholder="(555) 100-2000"
                                            className={classNames("w-full",{
                                                'p-invalid': fieldState.invalid,
                                                'text-red-500': fieldState.invalid,
                                            })} />
                                    </div>
                                    {
                                        fieldState.invalid ? (
                                            <small className="text-red-500">
                                                {fieldState.error?.message}
                                            </small>
                                        ) : null
                                    }
                                </>
                            )
                        }}
                    />
                </div>
            </div>
            <div className="flex flex-wrap flex-column gap-2 p-2">
                <label htmlFor="phoneNumber" className={classNames("font-medium", { "text-red-500": errors.deliveryAddress?.addressType })}>
                    Is this a residential or non-residential address?
                </label>    
                <div className="flex gap-3">
                    <Controller
                        name="deliveryAddress.addressType"
                        control={control}
                        render={({ field }) => (
                            <>
                                <div className="flex align-items-center gap-1">
                                    <RadioButton
                                        inputId="RESIDENTIAL"
                                        value="RESIDENTIAL"
                                        checked={field.value === "RESIDENTIAL"}
                                        onChange={() => {
                                            field.onChange("RESIDENTIAL");
                                        }}
                                    />
                                    <label htmlFor="RESIDENTIAL" className="ml-2 font-normal">Residential</label>
                                </div>
                                <div className="flex align-items-center gap-1">
                                    <RadioButton
                                        inputId="BUSINESS"
                                        value="BUSINESS"
                                        checked={field.value === "BUSINESS"}
                                        onChange={() => {
                                            field.onChange("BUSINESS");
                                        }}
                                    />
                                    <label htmlFor="BUSINESS" className="ml-2 font-normal"> Non-residential</label>
                                </div>
                            </>
                        )}
                    />
                </div>            
            </div>
        </form>
    );
})

export default RequestForm;